<template>
    <div class="compliance-management">
        <TargetPageHeader
            title="준수기준 및 목표 기준 관리"
            subtitle="목표 설정 시 준수 기준을 선택하고 관리할 때 사용합니다."
        >
            <template #actions>
                <button class="compliance-btn-add" @click="openAddModal"><i class="fa fa-plus"></i> 기준 추가</button>
            </template>
        </TargetPageHeader>

        <div class="compliance-table-container">
            <table class="compliance-table">
                <thead>
                    <tr>
                        <th>NO</th>
                        <th>기준 명칭</th>
                        <th>준수 기준 유형</th>
                        <th>방법론</th>
                        <th>시나리오</th>
                        <th>최소 감축률</th>
                        <th>버전</th>
                        <th>상태</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody v-if="loading">
                    <tr>
                        <td colspan="9" class="text-center">
                            <div class="spinner"></div>
                            로딩 중...
                        </td>
                    </tr>
                </tbody>
                <tbody v-else-if="complianceList.length === 0">
                    <tr>
                        <td colspan="9" class="text-center empty">등록된 준수기준이 없습니다.</td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr v-for="(item, index) in complianceList" :key="item.complianceIdx">
                        <td class="text-center">{{ index + 1 }}</td>
                        <td class="name-cell">
                            <div class="name-content">
                                <strong>{{ item.complianceName || item.complianceType }}</strong>
                                <small v-if="item.description">{{ item.description }}</small>
                            </div>
                        </td>
                        <td class="text-center">
                            <span class="badge badge-region">{{ getComplianceTypeLabel(item.complianceType) }}</span>
                        </td>
                        <td class="text-center">{{ getMethodologyLabel(item.methodology) }}</td>
                        <td class="text-center">{{ getScenarioLabel(item.scenario) }}</td>
                        <td class="text-center">{{ item.minReductRate ? item.minReductRate + "%" : "-" }}</td>
                        <td class="text-center">{{ item.version || "-" }}</td>
                        <td class="text-center">
                            <span :class="getStatusClass(item.status)">
                                {{ getStatusLabel(item.status) }}
                            </span>
                        </td>
                        <td class="text-center">
                            <div class="action-buttons">
                                <IconButton icon="fas fa-edit" title="수정" @click="openEditModal(item)" />
                                <IconButton
                                    icon="fas fa-trash"
                                    variant="delete"
                                    title="삭제"
                                    @click="deleteCompliance(item)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 추가/수정 모달 -->
        <b-modal
            v-model="showModal"
            :title="isEditMode ? '준수기준 수정' : '준수기준 추가'"
            size="lg"
            @hidden="resetForm"
        >
            <div class="modal-form">
                <div class="form-row">
                    <div class="form-group">
                        <label>기준 명칭 <span class="required">*</span></label>
                        <input
                            v-model="form.complianceName"
                            type="text"
                            class="form-control"
                            placeholder="예: K-ETS (Korea Emissions Trading Scheme)"
                        />
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>준수 기준 유형 <span class="required">*</span></label>
                        <select v-model="form.complianceType" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in complianceTypeOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>방법론</label>
                        <select v-model="form.methodology" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in methodologyOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group half">
                        <label>시나리오</label>
                        <select v-model="form.scenario" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in scenarioOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>최소 감축률 (%)</label>
                        <input
                            v-model.number="form.minReductRate"
                            type="number"
                            class="form-control"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            max="100"
                        />
                    </div>
                    <div class="form-group half">
                        <label>버전</label>
                        <input v-model="form.version" type="text" class="form-control" placeholder="예: v1.0" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>상세 설명</label>
                        <textarea
                            v-model="form.description"
                            class="form-control"
                            rows="3"
                            placeholder="준수 기준 상세 설명을 입력하세요"
                        ></textarea>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>상태</label>
                        <select v-model="form.status" class="form-control">
                            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>비고</label>
                        <textarea
                            v-model="form.remarks"
                            class="form-control"
                            rows="3"
                            placeholder="추가 설명을 입력하세요"
                        ></textarea>
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <button class="btn btn-secondary" @click="showModal = false">취소</button>
                <button class="btn btn-primary" @click="saveCompliance" :disabled="saving">
                    <span v-if="saving"><i class="fas fa-spinner fa-spin"></i> 저장 중...</span>
                    <span v-else>저장</span>
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import * as targetService from "../services/targetService";
import IconButton from "../../common/IconButton.vue";
import TargetPageHeader from "./TargetPageHeader.vue";
import CarbonCodes from "../../../codes";

export default {
    name: "ComplianceManagement",
    components: { IconButton, TargetPageHeader },
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            complianceList: [],
            loading: false,
            showModal: false,
            isEditMode: false,
            saving: false,
            form: {
                complianceIdx: null,
                complianceName: "",
                status: "ACTIVE",
                remarks: "",
            },
        };
    },
    computed: {
        complianceTypeOptions() {
            return CarbonCodes.complianceType.getList();
        },
        methodologyOptions() {
            return CarbonCodes.complianceMethodology.getList();
        },
        scenarioOptions() {
            return CarbonCodes.complianceScenario.getList();
        },
        statusOptions() {
            return CarbonCodes.status.getList();
        },
    },
    watch: {
        isActive: {
            immediate: true,
            async handler(val) {
                if (val) {
                    this.loadCompliances();
                }
            },
        },
    },
    methods: {
        async loadCompliances() {
            this.loading = true;
            try {
                this.complianceList = await targetService.fetchCompliances();
            } catch (error) {
                console.error("준수기준 목록 조회 실패:", error);
                this.$bvToast.toast("준수기준 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loading = false;
            }
        },
        openAddModal() {
            this.isEditMode = false;
            this.resetForm();
            this.showModal = true;
        },
        openEditModal(item) {
            this.isEditMode = true;
            this.form = { ...item };
            this.showModal = true;
        },
        async saveCompliance() {
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            try {
                let result;
                if (this.isEditMode) {
                    result = await targetService.updateCompliance(this.form.complianceIdx, this.form);
                } else {
                    result = await targetService.createCompliance(this.form);
                }
                this.$bvToast.toast(result.message || "저장되었습니다.", {
                    variant: "success",
                    title: "저장 완료",
                });
                this.showModal = false;
                await this.loadCompliances();
            } catch (error) {
                console.error("준수기준 저장 실패:", error);
                this.$bvToast.toast(error.message || "준수기준 저장에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.saving = false;
            }
        },
        async deleteCompliance(item) {
            const result = await this.alertConfirmWarning(`"${item.complianceName}" 기준을 삭제하시겠습니까?`);
            if (!result.value) return;

            try {
                const deleteResult = await targetService.deleteCompliance(item.complianceIdx);
                this.$bvToast.toast(deleteResult.data || "삭제되었습니다.", {
                    variant: "success",
                    title: "삭제 완료",
                });
                await this.loadCompliances();
            } catch (error) {
                console.error("준수기준 삭제 실패:", error);
                this.$bvToast.toast(error.message || "준수기준 삭제에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        validateForm() {
            if (!this.form.complianceName && !this.form.complianceType) {
                this.$bvToast.toast("기준 명칭 또는 유형을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.complianceType) {
                this.$bvToast.toast("준수 기준 유형을 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            return true;
        },
        resetForm() {
            this.form = {
                complianceIdx: null,
                complianceName: "",
                complianceType: "",
                methodology: "",
                scenario: "",
                minReductRate: null,
                version: "",
                description: "",
                status: "ACTIVE",
                remarks: "",
            };
        },
        getComplianceTypeLabel(value) {
            return CarbonCodes.complianceType.getLabel(value);
        },
        getMethodologyLabel(value) {
            return CarbonCodes.complianceMethodology.getLabel(value);
        },
        getScenarioLabel(value) {
            return CarbonCodes.complianceScenario.getLabel(value);
        },

        getStatusLabel(status) {
            return CarbonCodes.status.getLabel(status);
        },
        getStatusClass(status) {
            const normalized = (status || "").toLowerCase();
            return `badge badge-status badge-${normalized}`;
        },
    },
};
</script>

<style scoped>
.compliance-management {
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.compliance-btn-add {
    padding: 8px 15px;
    background: #52b788;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.compliance-btn-add:hover {
    background: #40916c;
    transform: translateY(-1px);
}

.compliance-btn-add i.fa-plus {
    margin-right: 6px;
}

.compliance-table-container {
    flex: 1;
    overflow: auto;
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
}

.compliance-table {
    width: 100%;
    border-collapse: collapse;
}

.compliance-table thead {
    background: #f9fafb;
}

.compliance-table th {
    padding: 8px 10px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
}

.compliance-table td {
    padding: 6px 10px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 13px;
    color: #4b5563;
}

.compliance-table td.text-center {
    text-align: center;
}

.compliance-table tbody tr:hover {
    background: #f9fafb;
}

.name-cell {
    min-width: 250px;
}

.name-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.name-content strong {
    color: #1f2937;
    font-weight: 600;
}

.name-content small {
    color: #9ca3af;
    font-size: 12px;
}

.badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.badge-region {
    background: #dbeafe;
    color: #1e40af;
}

.badge-status {
    padding: 6px 12px;
}

.badge-active {
    background: #d1fae5;
    color: #065f46;
}

.badge-inactive {
    background: #f3f4f6;
    color: #6b7280;
}

.badge-planned {
    background: #fef3c7;
    color: #92400e;
}

.action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.text-center {
    text-align: center;
}

.empty {
    padding: 48px 16px;
    color: #9ca3af;
}

.spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid #f3f4f6;
    border-top-color: #52b788;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-right: 8px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 모달 폼 스타일 */
.modal-form {
    padding: 8px 16px;
}

.form-row {
    display: flex;
    gap: 8px;
}

.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
}

.form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 3px;
}

.required {
    color: #ef4444;
}

.form-control {
    padding: 5px 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    transition: border-color 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary {
    background: #f3f4f6;
    color: #4b5563;
}

.btn-secondary:hover {
    background: #e5e7eb;
}

.btn-primary {
    background: #52b788;
    color: white;
}

.btn-primary:hover {
    background: #40916c;
}

.btn-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}
</style>
